import { __assign } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { Box } from '@mui/material';
// Coordinates of Seattle, avoid having the map centered in the middle of the sea
var DEFAULT_LATLNG = {
    lat: 47.6062,
    lng: -122.3321,
};
var DEFAULT_ZOOM_LEVEL = 3;
var DETAIL_ZOOM_LEVEL = 12;
var defaultMapOptions = {
    controlSize: 32,
    scrollwheel: false,
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    mapTypeControlOptions: {
        mapTypeIds: ['roadmap', 'hybrid'],
    },
    styles: [
        {
            elementType: 'all',
            featureType: 'all',
            stylers: [{ saturation: -50 }],
        },
        {
            elementType: 'all',
            featureType: 'administrative.country',
            stylers: [{ gamma: 2 }],
        },
        {
            elementType: 'all',
            featureType: 'administrative.locality',
            stylers: [{ gamma: 1.5 }],
        },
    ],
};
// Return map bounds based on list of markers
var getMapBounds = function (markers) {
    var bounds = new google.maps.LatLngBounds();
    markers.forEach(function (marker) {
        bounds.extend(new google.maps.LatLng(isNaN(marker.lat) ? 0 : marker.lat, isNaN(marker.lng) ? 0 : marker.lng));
    });
    return bounds;
};
// Fit map to its bounds after the api is loaded
var onGoogleMapsApiLoad = function (map, markers) {
    if (!markers.length) {
        map.setCenter(DEFAULT_LATLNG);
        map.setZoom(DEFAULT_ZOOM_LEVEL);
    }
    else {
        var bounds = getMapBounds(markers);
        map.fitBounds(bounds);
    }
    // These options depend on the global "google" object, so we set it once the API has loaded.
    map.setOptions({
        zoomControlOptions: {
            position: google.maps.ControlPosition.TOP_RIGHT,
        },
    });
    // If we run `setZoom` right after `fitBounds` the map won't refresh. With this we first wait for the map to be idle (from fitBounds), and then set the zoom level.
    var listener = google.maps.event.addListenerOnce(map, 'idle', function () {
        // Don't allow to zoom closer than the defailt detail zoom level on initial load.
        var currentZoom = map.getZoom();
        if (currentZoom != null && currentZoom > DETAIL_ZOOM_LEVEL) {
            map.setZoom(DETAIL_ZOOM_LEVEL);
        }
        google.maps.event.removeListener(listener);
    });
};
var getFieldFromMap = function (entry, fieldName, dataMap) {
    if (!dataMap[fieldName]) {
        return undefined;
    }
    if (typeof dataMap[fieldName] === 'string') {
        return entry[dataMap[fieldName]];
    }
    if (typeof dataMap[fieldName] === 'function') {
        return dataMap[fieldName](entry);
    }
};
var BaseMap = function (_a) {
    var className = _a.className, _b = _a.data, data = _b === void 0 ? [] : _b, dataMap = _a.dataMap, getIcon = _a.getIcon, onItemClick = _a.onItemClick, apiKey = _a.apiKey, mapClick = _a.mapClick;
    var markers = React.useMemo(function () {
        return data
            .map(function (entry) {
            var marker = {
                lng: getFieldFromMap(entry, 'lng', dataMap),
                lat: getFieldFromMap(entry, 'lat', dataMap),
                id: getFieldFromMap(entry, 'id', dataMap),
                title: getFieldFromMap(entry, 'title', dataMap),
                icon: getIcon ? getIcon(entry) : undefined,
                click: onItemClick ? function () { return onItemClick(entry); } : undefined,
            };
            if (!marker.lng || !marker.lat || !marker.id) {
                return null;
            }
            return marker;
        })
            .filter(function (x) { return !!x; });
    }, [data, dataMap, getIcon, onItemClick]);
    return (_jsx(Box, __assign({ height: "100%", className: className }, { children: apiKey && (_jsx(LoadScript, __assign({ googleMapsApiKey: apiKey, version: "3", language: "en" }, { children: _jsx(GoogleMap, __assign({ mapContainerStyle: {
                    height: '100%',
                    minHeight: '300px',
                    opacity: 1,
                }, options: defaultMapOptions, onLoad: function (map) { return onGoogleMapsApiLoad(map, markers); }, onClick: mapClick }, { children: markers.map(function (marker) { return (_jsx(Marker, { position: {
                        lat: isNaN(marker.lat) ? 0 : marker.lat,
                        lng: isNaN(marker.lng) ? 0 : marker.lng,
                    }, clickable: markers.length > 1, onClick: marker.click, title: marker.title, icon: marker.icon }, marker.id)); }) })) }))) })));
};
/** [View story source](https://github.com/balena-io-modules/rendition/blob/master/src/components/Map/Map.stories.tsx) */
export var Map = BaseMap;
